/**
 * /app/$tenantSlug/approvals — Inbox → Approvals (nav-build spec C; docs/71
 * §7 + §9; manager review 26 Oct P0-1/P0-2/P1-1/P1-2).
 *
 * One queue per request type the caller can act on, all in ONE layout
 * (components/inbox/approval-queues.tsx): the person leads each row, simple
 * requests carry inline Approve / Reject (Reject asks for a reason), heavy
 * ones a Review button, and every request row opens its detail page with
 * ?from=inbox.
 *
 *  - Tabs show only the types with pending work; every other type the
 *    caller may open sits behind "More". The active type is always a tab.
 *  - Every type opens on "Waiting on You". Types with a full list (leave,
 *    timesheets, expenses, travel) offer "History", which renders the
 *    module's own list component.
 *  - Nothing pending anywhere → one page-level "You're All Caught Up",
 *    with past requests one click away. No tab opens on an empty Leave
 *    list that blames the filter (P1-2).
 *  - "Out of Office" is a header button, for approvers only.
 *  - Phone: the tab bar is one row that scrolls sideways inside itself
 *    (edge fade, active tab kept in view via scrollLeft); filter rows wrap
 *    and never widen the page (P0-1, P0-2).
 *
 * Counts come from ONE role-scoped, own-items-excluded read (GET
 * /approvals/summary); each queue lists exactly what its count counts. A
 * tab is visible when the caller holds its gate OR it has pending work, so
 * a non-zero badge always has somewhere to land. Backend RBAC is the real
 * boundary; these gates are UX only.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import {
  createFileRoute,
  Link,
  Navigate,
  useNavigate,
  useParams,
} from "@tanstack/react-router";
import { z } from "zod";
import {
  Banknote,
  Briefcase,
  CalendarClock,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  ClipboardList,
  Clock,
  DoorOpen,
  FileCheck2,
  History,
  Home,
  MessageSquare,
  Plane,
  ReceiptIndianRupee,
  Repeat,
  Target,
  UserCheck,
} from "lucide-react";
import { useLeaveRoles } from "@/components/leave/use-leave-roles";
import { useExpenseRoles } from "@/components/expenses/use-expense-roles";
import { useTravelRoles } from "@/components/travel/use-travel-roles";
import { isAccountingAdmin, isHRAdmin } from "@/lib/nav";
import { useAuth, useFeatures } from "@/lib/auth";

import { LeaveApprovalsTab } from "@/components/leave/leave-approvals-tab";
import { ApprovalsTab as TimesheetApprovalsTab } from "@/components/timesheet/approvals-tab";
import { ExpensesTab } from "@/components/expenses/expenses-tab";
import { TravelPreapprovalList } from "@/components/travel/travel-preapproval-list";
import { ResignationsPanel } from "@/components/offboarding/resignations-panel";
import { JoiningDayQueue } from "@/components/preboarding/joining-day-queue";
import { useHiringApprovalSettings } from "@/hooks/use-hiring";
import {
  AttendanceQueue,
  ChangeRequestsQueue,
  ExpensesQueue,
  FeedbackQueue,
  HiringQueue,
  LeaveQueue,
  LoansQueue,
  ReviewsQueue,
  ShiftSwapQueue,
  TaxProofsQueue,
  TimesheetQueue,
  TravelQueue,
  WFHQueue,
} from "@/components/inbox/approval-queues";
import { InboxPage } from "@/components/inbox/inbox-page";
import { leavePagesQueryOptions } from "@/hooks/use-leave-request";
import {
  APPROVAL_TAB_KEYS,
  SUMMARY_KEY_BY_TAB,
  isApprovalsTabKey,
  type ApprovalsTabKey,
} from "@/lib/approval-tabs";
import { defaultApprovalTab, splitApprovalTabs } from "@/lib/inbox-status";
import {
  LEAVE_LIST_STATUSES,
  historyLeaveStatus,
  historyStatusParam,
} from "@/lib/leave-history-status";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/shared/empty-state";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { timesheetKeys } from "@/hooks/use-timesheet";
import { expenseKeys } from "@/hooks/use-expenses";
import { wfhKeys } from "@/hooks/use-wfh";
import { attendanceKeys } from "@/hooks/use-attendance";
import { shiftKeys } from "@/hooks/use-shifts";
import { loanKeys } from "@/hooks/use-loans";
import { travelPreapprovalKeys } from "@/hooks/use-travel-preapprovals";
import {
  APPROVALS_SUMMARY_QUERY_KEY,
  fetchApprovalsSummary,
  useApprovalsSummary,
} from "@/hooks/use-approvals-summary";
import {
  attendanceApi,
  expenseApi,
  loansApi,
  shiftsApi,
  timesheetApi,
  travelPreapprovalsApi,
  wfhApi,
} from "@/lib/api";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

// Tab set + summary-key map live in lib/approval-tabs.ts (shared with the
// Me-home queue; see the PARITY CONTRACT there).

// Mirrors the backend taxVerifyRole (company_admin, hr_manager, accountant).
const TAX_VERIFIER_ROLES = new Set(["company_admin", "hr_manager", "accountant"]);

const VIEWS = ["waiting", "history"] as const;
type ApprovalsView = (typeof VIEWS)[number];

const approvalsSearchSchema = z.object({
  tab: z.enum(APPROVAL_TAB_KEYS).optional().catch(undefined),
  view: z.enum(VIEWS).optional().catch(undefined),
  // Leave History's status filter, kept so Back from a request restores it.
  status: z.enum(LEAVE_LIST_STATUSES).optional().catch(undefined),
});

export const Route = createFileRoute("/_authgated/app/$tenantSlug/approvals")({
  validateSearch: approvalsSearchSchema,
  // A9 hover-prefetch: the counts read first, then each "Waiting on You"
  // list under the exact key its queue reads (fire-and-forget). The hiring
  // feed is not prefetched — it 403s anyone but the configured approver.
  loader: ({ context }) => {
    const qc = context.queryClient;
    void qc.prefetchQuery({
      queryKey: APPROVALS_SUMMARY_QUERY_KEY,
      queryFn: ({ signal }) => fetchApprovalsSummary(signal),
    });
    // The leave tab's list is paged (wave 2) — prefetch its first page
    // under the same key LeaveRequestList reads.
    void qc.prefetchInfiniteQuery(
      leavePagesQueryOptions({ status: "pending", exclude_self: true }, "excl-self"),
    );
    // Timesheet is the one panel that does NOT pass exclude_self: HR may
    // approve their own submitted week (no self-decision block server-side),
    // so the caller's own submission is genuinely actionable — and the
    // /approvals/summary timesheet count includes it for the same reason.
    void qc.prefetchQuery({
      queryKey: timesheetKeys.inboxList("submitted"),
      queryFn: ({ signal }) =>
        timesheetApi.getInbox({ status: "submitted" }, signal),
    });
    void qc.prefetchQuery({
      queryKey: expenseKeys.list("submitted", undefined, undefined, "excl-self"),
      queryFn: ({ signal }) =>
        expenseApi.listExpenses(
          { status: "submitted", exclude_self: true },
          signal,
        ),
    });
    void qc.prefetchQuery({
      queryKey: wfhKeys.requests("pending", undefined, true),
      queryFn: ({ signal }) =>
        wfhApi.listWFHRequests(
          { status: "pending", exclude_self: true },
          signal,
        ),
    });
    void qc.prefetchQuery({
      queryKey: attendanceKeys.regularizations("pending", undefined, true),
      queryFn: ({ signal }) =>
        attendanceApi.listRegularizations(
          { status: "pending", exclude_self: true },
          signal,
        ),
    });
    void qc.prefetchQuery({
      queryKey: shiftKeys.swaps("pending_manager", undefined, true),
      queryFn: ({ signal }) =>
        shiftsApi.listSwaps(
          { status: "pending_manager", exclude_self: true },
          signal,
        ),
    });
    void qc.prefetchQuery({
      queryKey: loanKeys.list("requested", undefined, true),
      queryFn: ({ signal }) =>
        loansApi.listLoans(
          { status: "requested", exclude_self: true },
          signal,
        ),
    });
    void qc.prefetchQuery({
      queryKey: travelPreapprovalKeys.list("pending", true),
      queryFn: ({ signal }) =>
        travelPreapprovalsApi.listTravelPreapprovals(
          { status: "pending", exclude_self: true },
          signal,
        ),
    });
  },
  component: ApprovalsRoute,
});

const ICONS: Record<ApprovalsTabKey, typeof CalendarDays> = {
  leave: CalendarDays,
  timesheet: Clock,
  expenses: ReceiptIndianRupee,
  wfh: Home,
  attendance: CalendarClock,
  shifts: Repeat,
  loans: Banknote,
  travel: Plane,
  hiring: Briefcase,
  change_requests: ClipboardList,
  reviews: Target,
  feedback: MessageSquare,
  tax_proofs: FileCheck2,
  resignations: DoorOpen,
  joiners: UserCheck,
};

// Title Case on Apple's split (docs/71 §11).
const LABELS: Record<ApprovalsTabKey, string> = {
  leave: "Leave",
  timesheet: "Timesheets",
  expenses: "Expenses",
  wfh: "Work From Home",
  attendance: "Attendance",
  shifts: "Shift Swaps",
  loans: "Loans",
  travel: "Travel",
  hiring: "Hiring",
  change_requests: "Change Requests",
  reviews: "Reviews",
  feedback: "Feedback",
  tax_proofs: "Tax Proofs",
  resignations: "Resignations",
  joiners: "Joiners",
};

/** One plain sentence above each queue. */
const HELPERS: Record<ApprovalsTabKey, string> = {
  leave: "Leave requests from the people you approve for.",
  timesheet: "Submitted weeks waiting for your sign-off.",
  expenses: "Expense claims waiting for your decision. Open one to send it back for changes.",
  wfh: "Work from home requests waiting for your decision.",
  attendance: "Punch corrections waiting for your decision.",
  shifts: "Swaps you've been asked to accept or approve.",
  loans: "Loan and advance requests. Open one to approve, reject or disburse it.",
  travel: "Trips to pre-approve. Open one to set the budget.",
  hiring: "New jobs and offers waiting for your sign-off.",
  change_requests: "Changes to employee records. Open one to check the documents.",
  reviews: "Performance reviews you owe in open review cycles.",
  feedback: "Colleagues who asked for your feedback.",
  tax_proofs: "Investment proofs submitted for verification.",
  resignations: "Accept to start the exit, or close it if the employee is staying.",
  joiners: "People whose joining day has come. Confirm they joined, or move the date.",
};

/** The "Waiting on You" queue for each type. */
function WaitingQueue({ tab }: Readonly<{ tab: ApprovalsTabKey }>) {
  switch (tab) {
    case "leave":
      return <LeaveQueue />;
    case "timesheet":
      return <TimesheetQueue />;
    case "expenses":
      return <ExpensesQueue />;
    case "wfh":
      return <WFHQueue />;
    case "attendance":
      return <AttendanceQueue />;
    case "shifts":
      return <ShiftSwapQueue />;
    case "loans":
      return <LoansQueue />;
    case "travel":
      return <TravelQueue />;
    case "hiring":
      return <HiringQueue />;
    case "change_requests":
      return <ChangeRequestsQueue />;
    case "reviews":
      return <ReviewsQueue />;
    case "feedback":
      return <FeedbackQueue />;
    case "tax_proofs":
      return <TaxProofsQueue />;
    case "resignations":
      return <ResignationsPanel inbox />;
    case "joiners":
      return <JoiningDayQueue />;
  }
}

/** What a History list may keep in the URL (leave's status filter). */
interface HistoryCtx {
  readonly status: string | undefined;
  readonly setStatus: (status: string) => void;
}

/** Types whose module has a full list (decided requests too). */
const HISTORY: Partial<Record<ApprovalsTabKey, (ctx: HistoryCtx) => ReactNode>> = {
  leave: (ctx) => (
    <LeaveApprovalsTab status={historyLeaveStatus(ctx.status)} onStatusChange={ctx.setStatus} />
  ),
  timesheet: () => <TimesheetApprovalsTab />,
  expenses: () => <ExpensesTab mode="approver" />,
  travel: () => <TravelPreapprovalList mode="approver" />,
};

function CountBadge({ count }: Readonly<{ count: number }>) {
  if (count <= 0) return null;
  return (
    <span className="ml-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary/10 px-1.5 text-xs font-medium text-primary tabular-nums">
      {count}
      <span className="sr-only"> waiting</span>
    </span>
  );
}

function ApprovalsRoute() {
  const { user } = useAuth();
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  // Who may set up out-of-office cover (mirrors the backend's
  // isDelegatingApprover — a pure delegate cannot re-delegate).
  const canDelegate =
    isHRAdmin(user?.role) || user?.role === "manager" || user?.has_reports === true;
  return (
    <InboxPage
      title="Approvals"
      description="Requests waiting on your decision."
      actions={
        canDelegate ? (
          <Button asChild variant="outline" className="gap-1.5">
            <Link to="/app/$tenantSlug/approval-delegation" params={{ tenantSlug }}>
              <Plane className="h-4 w-4" />
              Out of Office
            </Link>
          </Button>
        ) : undefined
      }
    >
      <ApprovalsBody />
    </InboxPage>
  );
}

function PageSkeleton() {
  return (
    <div className="space-y-3">
      <Skeleton className="h-10 w-full max-w-md" />
      <Skeleton className="h-20 w-full" />
      <Skeleton className="h-20 w-full" />
    </div>
  );
}

/**
 * A one-row tab bar that scrolls sideways inside itself (never the page).
 * The active tab is kept in view by setting scrollLeft (docs/71 §7, not
 * scrollIntoView, which would also scroll the page), and a fade marks
 * more tabs to the right.
 */
function TabStrip({
  activeKey,
  children,
}: Readonly<{ activeKey: string; children: ReactNode }>) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [fade, setFade] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const active = el.querySelector<HTMLElement>('[role="tab"][data-state="active"]');
    if (active) {
      const left = active.offsetLeft;
      const right = left + active.offsetWidth;
      if (left < el.scrollLeft) el.scrollLeft = Math.max(0, left - 8);
      else if (right > el.scrollLeft + el.clientWidth) {
        el.scrollLeft = right - el.clientWidth + 8;
      }
    }
    const update = () => setFade(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    update();
    el.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      el.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [activeKey]);
  return (
    <div className="relative min-w-0 flex-1">
      <div
        ref={ref}
        className="relative overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {children}
      </div>
      {fade && (
        <span
          aria-hidden
          className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-background to-transparent"
        />
      )}
    </div>
  );
}

function ApprovalsBody() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const search = Route.useSearch();
  const navigate = useNavigate();

  // Role / standing gates (UX only — backend re-checks). The leave-style
  // approver flag is the caller's STANDING (approver role, team lead with
  // active reports, or a live out-of-office delegate).
  const { isApprover } = useLeaveRoles();
  const { isApprover: canExpenses } = useExpenseRoles();
  const { isApprover: canTravel } = useTravelRoles();
  const { user } = useAuth();
  const canLoans = isAccountingAdmin(user?.role);
  const hrAdmin = isHRAdmin(user?.role);
  const { has } = useFeatures();
  // Hiring approval: only the tenant's CONFIGURED approver role has a queue.
  const hiringSettings = useHiringApprovalSettings(hrAdmin);
  const canHiring =
    hrAdmin && hiringSettings.data?.settings.approver_role === user?.role;
  const canTaxProofs = !!user?.role && TAX_VERIFIER_ROLES.has(user.role);

  const summaryQ = useApprovalsSummary();
  const byType = summaryQ.data?.by_type;
  const counts = Object.fromEntries(
    APPROVAL_TAB_KEYS.map((k) => [k, byType?.[SUMMARY_KEY_BY_TAB[k]] ?? 0]),
  ) as Record<ApprovalsTabKey, number>;

  const gates: Record<ApprovalsTabKey, boolean> = {
    leave: isApprover,
    timesheet: isApprover,
    expenses: canExpenses,
    wfh: isApprover,
    attendance: isApprover,
    shifts: isApprover,
    loans: canLoans,
    travel: canTravel,
    hiring: canHiring,
    change_requests: hrAdmin,
    // Reviews / feedback are owed by anyone — shown when there is some.
    reviews: false,
    feedback: false,
    tax_proofs: canTaxProofs,
    resignations: hrAdmin,
    joiners: hrAdmin && has("hr_core"),
  };
  const visibleKey = APPROVAL_TAB_KEYS.filter((k) => gates[k] || counts[k] > 0).join(",");
  const visibleTabs = useMemo(
    () => (visibleKey ? (visibleKey.split(",") as ApprovalsTabKey[]) : []),
    [visibleKey],
  );
  const totalPending = visibleTabs.reduce((n, k) => n + counts[k], 0);

  const activeTab: ApprovalsTabKey | undefined =
    search.tab && visibleTabs.includes(search.tab)
      ? search.tab
      : defaultApprovalTab(visibleTabs, counts);
  const view: ApprovalsView =
    search.view === "history" && activeTab && HISTORY[activeTab] ? "history" : "waiting";

  const go = useCallback(
    (tab: ApprovalsTabKey | undefined, nextView?: ApprovalsView) => {
      void navigate({
        to: "/app/$tenantSlug/approvals",
        params: { tenantSlug },
        search: {
          ...(tab ? { tab } : {}),
          ...(nextView === "history" ? { view: "history" as const } : {}),
        },
        replace: true,
      });
    },
    [navigate, tenantSlug],
  );

  // Leave History's status filter, in the URL (lib/leave-history-status).
  const setHistoryStatus = useCallback(
    (status: string) => {
      void navigate({
        to: "/app/$tenantSlug/approvals",
        params: { tenantSlug },
        search: {
          ...(activeTab ? { tab: activeTab } : {}),
          view: "history" as const,
          ...historyStatusParam(status),
        },
        replace: true,
      });
    },
    [navigate, tenantSlug, activeTab],
  );

  // Wait for the counts before choosing what to show (an explicit ?tab the
  // caller can open renders straight away; any other ?tab falls back to the
  // default once the counts are in).
  if (summaryQ.isPending && !(search.tab && visibleTabs.includes(search.tab))) {
    return <PageSkeleton />;
  }

  // Someone who decides nothing (an old link, a bookmark) has no Approvals
  // tab: their Inbox is Tasks.
  if (visibleTabs.length === 0) {
    return <Navigate to="/app/$tenantSlug/tasks" params={{ tenantSlug }} replace />;
  }

  const historyTypes = visibleTabs.filter((k) => HISTORY[k]);

  // Nothing waiting anywhere and no type picked: one page-level state.
  if (!activeTab) {
    return (
      <EmptyState
        icon={CheckCircle2}
        title="You're All Caught Up"
        description={
          visibleTabs.length === 0
            ? "Requests from people who report to you, shift swaps you're asked to accept, reviews you owe and feedback you're asked for show up here."
            : "Nothing is waiting on your decision."
        }
        action={
          historyTypes.length > 0 ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="gap-1.5">
                  <History className="h-4 w-4" />
                  Past Requests
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center">
                {historyTypes.map((k) => {
                  const Icon = ICONS[k];
                  return (
                    <DropdownMenuItem key={k} onSelect={() => go(k, "history")} className="gap-2">
                      <Icon className="h-4 w-4" />
                      {LABELS[k]}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : undefined
        }
      />
    );
  }

  const { primary, more } = splitApprovalTabs(visibleTabs, counts, activeTab);
  const history = HISTORY[activeTab];

  return (
    <Tabs
      value={activeTab}
      onValueChange={(v) => go(isApprovalsTabKey(v) ? v : undefined)}
      className="space-y-4"
    >
      {totalPending === 0 && (
        <p className="text-sm text-muted-foreground">
          You&apos;re all caught up. Nothing is waiting on your decision.
        </p>
      )}
      <div className="flex items-center gap-2">
        <TabStrip activeKey={activeTab}>
          <TabsList className="h-auto w-max justify-start">
            {primary.map((k) => {
              const Icon = ICONS[k];
              return (
                <TabsTrigger key={k} value={k} className="min-h-9 gap-1.5">
                  <Icon aria-hidden className="h-4 w-4" />
                  {LABELS[k]}
                  <CountBadge count={counts[k]} />
                </TabsTrigger>
              );
            })}
          </TabsList>
        </TabStrip>
        {more.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-10 shrink-0 gap-1">
                More
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Nothing Waiting</DropdownMenuLabel>
              {more.map((k) => {
                const Icon = ICONS[k];
                return (
                  <DropdownMenuItem key={k} onSelect={() => go(k)} className="gap-2">
                    <Icon className="h-4 w-4" />
                    {LABELS[k]}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      <TabsContent value={activeTab} className="mt-0 space-y-4">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="min-w-0 text-sm text-muted-foreground">{HELPERS[activeTab]}</p>
          {history && (
            <Select
              value={view}
              onValueChange={(v) => go(activeTab, v === "history" ? "history" : "waiting")}
            >
              <SelectTrigger className="w-full sm:w-44" aria-label="Show">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="waiting">Waiting on You</SelectItem>
                <SelectItem value="history">History</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>
        <div className={cn(view === "history" && "min-w-0")}>
          {view === "history" && history ? (
            history({ status: search.status, setStatus: setHistoryStatus })
          ) : (
            <WaitingQueue tab={activeTab} />
          )}
        </div>
      </TabsContent>
    </Tabs>
  );
}
